/**
 * The send guard, said out loud, wherever a person is about to commit to a send.
 *
 * **Why this component exists at all.** This environment's database holds real
 * contacts, and the guard means a campaign to 1,278 people will deliver almost
 * nothing — *and that is correct*. Somebody who schedules that campaign and then
 * reads "1 sent, 1,277 skipped" without having been told will conclude the
 * product is broken, file it, and be right to. So the guard is described before
 * the button is pressed, in the same panel as the button, with this campaign's
 * own numbers in it.
 *
 * **The tone is deliberately calm.** Not a red alert, not an amber triangle:
 * nothing is wrong. The guard is working, it is the ordinary state of every
 * environment that is not production, and it is the thing standing between a
 * development database and a thousand real organisations. A warning colour on
 * the expected outcome is how a person learns to scroll past the warnings that
 * are not expected — the SES sandbox notice, usually sitting directly above it.
 *
 * **It cannot leak an allow-list.** The shape it renders carries a *count* and
 * never the addresses: `sendGuardStatusSchema` has no field for them, on
 * purpose, because shipping the operator's test addresses to a browser answers a
 * question nobody asked. The addresses that will actually receive a campaign are
 * shown once they exist — on the recipient list, where they are this workspace's
 * own contacts rather than someone's configuration.
 */
import type { CampaignReach, SendGuardStatus } from '@wend/shared';
import { Callout, type CalloutLive } from '@wend/ui';
import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  SEND_GUARD_ALWAYS_ON_BODY,
  SEND_GUARD_ALWAYS_ON_TITLE,
  readSendGuard,
} from '../lib/campaign-display';

export interface SendGuardNoticeProps {
  guard: SendGuardStatus;
  /** The reach the guard is cutting down — the channel reach, never the segment. */
  reach: CampaignReach;
  /** A link to the recipients, once there are any. */
  action?: ReactNode;
  live?: CalloutLive;
}

/**
 * What the guard will do to this campaign.
 *
 * The headline is the number: "1 of 526 will actually receive this". The body
 * says where the other 525 went and that being told about them is the point —
 * every one of them becomes a skipped recipient carrying a reason, on a list
 * this screen links to.
 */
export function SendGuardNotice({ guard, reach, action, live = 'off' }: SendGuardNoticeProps) {
  const reading = readSendGuard(guard, reach);

  return (
    <Callout
      tone={reading.tone}
      title={reading.headline}
      icon={<ShieldCheck aria-hidden="true" />}
      live={live}
      {...(action === undefined ? {} : { action })}
    >
      {/*
        THE SECOND PARAGRAPH WENT — SLICE 28.

        It described the internal evaluation schedule of a safety mechanism:
        *"The guard runs twice — once when the recipient list is built, and again
        immediately before each message is handed to Amazon."* Nobody acts on
        that. Not the sender, who cannot change the allow-list, and not the
        operator either, who cannot change WHEN the guard runs.

        It was also wrong on one channel: the sentence was hard-coded rather than
        keyed by channel, so it said "handed to Amazon" on a WhatsApp campaign.

        `reading.explanation` stays. It is the half with this campaign's own
        numbers in it, which is the half a sender can act on.
      */}
      <p>{reading.explanation}</p>
    </Callout>
  );
}

/**
 * The guard before any campaign exists, on the create screen.
 *
 * It carries no numbers because there are none to carry yet: the deliverable
 * count is an intersection of this segment's addresses with the allow-list, and
 * the API computes it against a campaign. What it *can* say without a request is
 * the part that is unconditionally true — the guard is on, it cannot be turned
 * off from this app, and an empty allow-list delivers nothing at all.
 *
 * That is not a hedge. `sendGuardStatusSchema.enforced` is `z.literal(true)`, so
 * the contract has no way to express a guard that is off; a build with the guard
 * lifted fails to parse its own readiness rather than quietly claiming a
 * protection it is not applying.
 */
export function SendGuardAlwaysOnNotice() {
  return (
    <Callout
      tone="info"
      title={SEND_GUARD_ALWAYS_ON_TITLE}
      icon={<ShieldCheck aria-hidden="true" />}
    >
      {SEND_GUARD_ALWAYS_ON_BODY}
    </Callout>
  );
}
